import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import { useQuery } from "@apollo/client";
import CategoryBusinessCard from "../../../pages/customer/categoryBusinessCard";
import ExternalTimelyRoute from "../../../../apps/plany-care/integrations/booking/ExternalTimelyRoute";
import { SEARCH } from "../../../graphql/queries";
import { BusinessData } from "../../../types";
import { US_STATE_CODES, US_STATE_NAMES } from "../../../util/stateCity";

/* ------------------------------ Utils ---------------------------------- */

const str = (v?: string | null) => (typeof v === "string" ? v : "");
const trim = (v: string) => v.trim();
const lc = (v: string) => v.toLowerCase().trim();

// Final location string that the backend gets
function normalizeLocationForBackend(input: string) {
  return (input || "").trim();
}

/** Heuristic: does a string look like a location? */
function isLocationLike(input: string): boolean {
  const q = lc(trim(input));
  if (!q) return false;

  // ZIP: 3+ digits
  const qZip = q.replace(/\s|-/g, "");
  if (/^\d{3,}$/.test(qZip)) return true;

  // State code (exact 2-letter)
  if (/^[a-z]{2}$/.test(q) && US_STATE_CODES.has(q.toUpperCase())) return true;

  // State full name
  if (US_STATE_NAMES.has(q)) return true;

  // "seattle, wa"
  if (/,/.test(q)) {
    const [city, state] = q.split(",").map((p) => p.trim());
    if (city && state) {
      if (US_STATE_CODES.has(state.toUpperCase()) || US_STATE_NAMES.has(state))
        return true;
    }
  } else {
    // "seattle wa"
    const tokens = q.split(/\s+/);
    if (tokens.length >= 2) {
      const last = tokens[tokens.length - 1];
      if (US_STATE_CODES.has(last.toUpperCase()) || US_STATE_NAMES.has(last))
        return true;
    }
  }
  return false;
}

/** Compose a nice label for the Where box from various params. */
function composeLocationLabel(opts: {
  locationParam?: string;
  city?: string;
  state?: string;
}) {
  const { locationParam, city, state } = opts;
  const loc = trim(str(locationParam));
  const c = trim(str(city));
  const s = trim(str(state));
  if (loc) return loc;
  if (c && s) return `${c}, ${s}`;
  if (c) return c;
  if (s) return s;
  return "";
}

/** Parse "street, city, ST, zip" (for client-side city filtering). */
function parseAddressString(addr?: string) {
  if (typeof addr !== "string")
    return { street: "", city: "", state: "", zip: "" };
  const parts = addr
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  return {
    street: parts[0] || "",
    city: parts[1] || "",
    state: parts[2] || "",
    zip: parts[3] || "",
  };
}

/** Legacy "displayable" rule */
const isDisplayable = (b: any) =>
  b?.displayOnBookingPage === true ||
  b?.businessBookingTypes?.some(
    (t: { displayOnBookingPage: boolean }) => t.displayOnBookingPage === true
  );

const hasPublicBookingListing = (b: any) =>
  (b?.businessBasic?.displayOnBookingPage === true ||
    b?.displayOnBookingPage === true) &&
  (b?.businessBookingTypes || b?.businessBasic?.businessBookingTypes || []).some(
    (t: any) => t?.isPublic === true
  );

/* ------------------------------ Component ------------------------------ */

const SearchPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isBookingSearch = searchParams.get("v") === "booking";

  // Controlled inputs
  const [searchQuery, setSearchQuery] = useState(""); // WHAT
  const [locationQuery, setLocationQuery] = useState(""); // WHERE
  const [dateQuery, setDateQuery] = useState("");

  // Raw params to drive backend & client-side fallback
  const [rawCityParam, setRawCityParam] = useState("");
  const [rawStateParam, setRawStateParam] = useState("");

  // Read querystring / route and prefill boxes
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Legacy params
    const qParam = str(params.get("q"));
    const locParamOld = str(params.get("location")); // legacy
    const dateParam = str(params.get("date"));

    // Newer helpers (from home chips/links)
    const locParamNew = str(params.get("loc")); // optional new
    const cityName = str(params.get("city") || params.get("name"));
    const stateNameOrCode = str(params.get("state"));

    setRawCityParam(cityName);
    setRawStateParam(stateNameOrCode);

    // Build default WHERE from explicit location params or city/state
    const whereFromParams = composeLocationLabel({
      locationParam: trim(locParamOld) || trim(locParamNew),
      city: cityName,
      state: stateNameOrCode,
    });

    // If explicit WHERE provided, use it.
    if (whereFromParams) {
      setLocationQuery(whereFromParams);
      setSearchQuery(qParam);
    } else {
      // No explicit WHERE; decide where to put qParam.
      if (qParam && isLocationLike(qParam)) {
        setLocationQuery(qParam); // location-like -> WHERE
        setSearchQuery("");
      } else {
        setSearchQuery(qParam); // provider-like -> WHAT
        setLocationQuery("");
      }
    }

    setDateQuery(dateParam);
  }, [location.search]);

  // Build backend location query: prefer the WHERE box; otherwise fallback to city/state params
  const backendLocation = useMemo(() => {
    const fromWhere = trim(locationQuery);
    if (fromWhere) return lc(normalizeLocationForBackend(fromWhere));

    const c = rawCityParam;
    const s = trim(rawStateParam);
    if (c && s) return lc(normalizeLocationForBackend(`${c}, ${s}`));
    if (c) return lc(normalizeLocationForBackend(c));
    if (s) return lc(normalizeLocationForBackend(s));
    return "";
  }, [locationQuery, rawCityParam, rawStateParam]);

  // GraphQL call (contract unchanged)
  const { loading, data, error } = useQuery(SEARCH, {
    variables: {
      query: JSON.stringify({
        searchQuery: lc(searchQuery),
        locationQuery: backendLocation, // <-- now uses city/state fallback
        dateQuery,
        countryId: localStorage.getItem("currentCountryId"),
      }),
    },
  });

  // Submit → update URL (keep legacy param names for compatibility)
  // Visible list with legacy "displayable" filter + optional client-side city narrowing
  const visible = useMemo(() => {
    const rows: any[] = data?.search || [];
    const base = rows.filter(isBookingSearch ? hasPublicBookingListing : isDisplayable);

    const normCity = lc(rawCityParam);
    if (!normCity) return base;

    // If a specific city was requested, prefer items whose parsed city matches.
    return base.filter((b) => {
      const addr =
        (b as any)?.businessBasic?.address ??
        (b as any)?.address ??
        (b as any)?.addressText ??
        "";
      const parsed = parseAddressString(addr);
      const city = lc(parsed.city);
      return city === normCity || city.includes(normCity);
    });
  }, [data?.search, isBookingSearch, rawCityParam]);

  return (
    <>
      {/* Results */}
      {!loading && !error && (
        <>
          {isBookingSearch ? (
            <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
              <ExternalTimelyRoute />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
              {visible.map((business: BusinessData) => {
                const id =
                  (business as any)?.businessBasic?.id ?? (business as any)?.id;
                const url = (business as any)?.businessBasic?.url;
                return (
                  <Grid key={id} item xs={12}>
                    <CategoryBusinessCard business={business} url={url} />
                  </Grid>
                );
              })}
            </Grid>
          )}

          {visible.length === 0 && (
            <Box textAlign="center" mt={4} fontSize="1rem">
              {isBookingSearch
                ? "No public booking businesses matched your search yet."
                : "No businesses matched your search or are public to display."}
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default SearchPage;
